/* Bhairav Tatva — layer around the gemstone configurator (assets/gemstone-configurator.js, kept as is).
   Loaded on every page from bt-brand (the cart clean-up refresh below is needed everywhere).
   - Icons on Gemstone / Ring / Pendant, metal swatches, and the certificate dropdown shown as cards:
     the cards only drive GJ's own <select>, so GJ's state and cart properties are untouched.
   - Horizon cart: after the configurator (or GJ's bundle clean-up) changes the cart, tell Horizon so the
     cart count updates and the drawer opens / refreshes. GJ's own refresh code targets Dawn only.
   - "Buy now" next to "Add to cart": adds the configured bundle, then goes to checkout. (A cart permalink
     can't carry the _gj_* line properties the configurator reads, so it has to go through the cart.)
   - The sticky bar's "Add to cart" / "Buy now" go through the configurator too, so no order skips the
     certificate / setting choice. If an option still has to be picked, it scrolls to the options. */
import { CartLinesUpdateEvent } from '@shopify/events';

const wrap = document.querySelector('.bt-cfg');
const root = document.getElementById('gj-gemstone-configurator');
const routes = (window.Shopify && window.Shopify.routes) || {};
const base = routes.root || '/';

async function tellHorizon(action, lines, target) {
  const deferred = CartLinesUpdateEvent.createPromise();
  (target || document).dispatchEvent(new CartLinesUpdateEvent({ action, context: 'product', lines, promise: deferred.promise }));
  try {
    const cart = await fetch(`${base}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, source: 'gj-configurator', didError: false },
    });
  } catch (err) {
    deferred.reject(err);
  }
}

// GJ removed orphaned setting/certificate lines after the stone was taken out of the cart (any page)
document.addEventListener('gj:bundle:reconciled', () => tellHorizon('update', []));

if (wrap && root) {
  let buying = false;
  let buyTimer = 0;
  const submit = () => root.querySelector('[data-submit]');
  const resetBuy = () => {
    buying = false;
    clearTimeout(buyTimer);
    const b = root.querySelector('.bt-cfg__buy');
    if (b) { b.textContent = 'Buy now'; b.disabled = submit() ? submit().disabled : false; }
  };

  document.addEventListener('gj:configurator:added', (e) => {
    if (buying) {
      window.location.href = `${base}checkout`;
      return;
    }
    const items = (e.detail && e.detail.items) || [];
    const lines = (Array.isArray(items) ? items : []).map((it) => ({ merchandiseId: String(it.variant_id || it.id), quantity: it.quantity || 1 }));
    tellHorizon('add', lines, root);
  });

  const buyNow = () => {
    const s = submit();
    if (!s || s.disabled) { showOptions(); return; }
    buying = true;
    const b = root.querySelector('.bt-cfg__buy');
    if (b) { b.disabled = true; b.textContent = 'Opening checkout…'; }
    buyTimer = setTimeout(resetBuy, 12000);
    s.click();
  };

  const showOptions = () => {
    wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
    wrap.classList.remove('is-flash');
    void wrap.offsetWidth;
    wrap.classList.add('is-flash');
    const first = root.querySelector('#gj-ring-size:not([disabled])');
    if (first && first.offsetParent && !first.value) setTimeout(() => first.focus({ preventScroll: true }), 500);
  };

  const ICONS = {
    Gemstone: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4h10l4 5-9 11L3 9z"/><path d="M3 9h18M9 4l3 5 3-5M12 9v11"/></svg>',
    Ring: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="15" r="6"/><path d="M9 9.6 7.6 6.2 10 3.5h4l2.4 2.7L15 9.6"/></svg>',
    Pendant: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 3c1.5 4.5 4.5 7 8 7s6.5-2.5 8-7"/><path d="M12 10v1.5"/><path d="M12 11.5l3.5 4.5-3.5 5-3.5-5z"/></svg>',
  };
  const cleanCertName = (n) => String(n || '').replace(/\s*\(free\)\s*/i, ' ').replace(/\s+-\s+(same day|\d+\s*days?)\s*$/i, '').replace(/\s{2,}/g, ' ').trim();
  const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  // Customer-facing wording for GemJohri's form step. Only the text changes: data-form values,
  // cart properties and orders stay exactly as GemJohri sends them.
  const FORM_LABELS = { Gemstone: 'Loose stone', Ring: 'Set in a ring', Pendant: 'Set in a pendant' };
  const FORM_HEADING = 'Want it in a ring or pendant?';

  const decorateForms = () => {
    const title = root.querySelector('.gj-configurator__title');
    if (title && !title.dataset.btText) { title.dataset.btText = '1'; title.textContent = FORM_HEADING; }
    const formLabel = root.querySelector('[data-section="form"] > .gj-configurator__label');
    if (formLabel) formLabel.remove();
    const formGroup = root.querySelector('[data-section="form"] [role="radiogroup"]');
    if (formGroup) formGroup.setAttribute('aria-label', FORM_HEADING);
    root.querySelectorAll('[data-form]').forEach((b) => {
      const label = FORM_LABELS[b.dataset.form];
      if (label && !b.dataset.btText) { b.dataset.btText = '1'; b.textContent = label; }
    });
    root.querySelectorAll('[data-form]').forEach((b) => {
      if (b.querySelector('.bt-cfg__ico')) return;
      const svg = ICONS[b.dataset.form];
      if (!svg) return;
      const i = document.createElement('span');
      i.className = 'bt-cfg__ico';
      i.innerHTML = svg;
      b.prepend(i);
    });
    // Metal swatches: a small coloured disc for Copper, Brass, Panchdhatu, Ashtadhatu, Silver
    root.querySelectorAll('[data-metal]').forEach((b) => {
      if (b.querySelector('.bt-cfg__sw')) return;
      const sw = document.createElement('span');
      sw.className = `bt-cfg__sw bt-cfg__sw--${String(b.dataset.metal || '').toLowerCase().replace(/[^a-z]/g, '')}`;
      sw.setAttribute('aria-hidden', 'true');
      b.prepend(sw);
    });
  };

  const certCards = () => {
    const sel = root.querySelector('#gj-certificate');
    if (!sel || sel.dataset.btCards) return;
    sel.dataset.btCards = '1';
    let cfg = {};
    try { cfg = JSON.parse(document.getElementById('gj-gemstone-configurator-config').textContent); } catch (e) { /* GJ shows its own error */ }
    const certs = (cfg && cfg.certificates) || [];
    if (!certs.length) return;
    const section = sel.closest('.gj-configurator__section');
    const list = document.createElement('div');
    list.className = 'bt-certs';
    list.setAttribute('role', 'radiogroup');
    list.setAttribute('aria-label', 'Certificate');
    const cards = certs.map((c) => {
      const free = !(Number(c.price) > 0);
      const days = Number(c.extra_days) || 0;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bt-cert';
      b.setAttribute('role', 'radio');
      b.dataset.value = c.id;
      b.innerHTML = '<span class="bt-cert__dot" aria-hidden="true"></span><span class="bt-cert__body"><span class="bt-cert__name"></span><span class="bt-cert__meta"></span></span><span class="bt-cert__price"></span>';
      b.querySelector('.bt-cert__name').textContent = cleanCertName(c.name);
      b.querySelector('.bt-cert__meta').textContent = days ? `Adds ${days} day${days > 1 ? 's' : ''} before dispatch` : 'Ships with your stone';
      b.querySelector('.bt-cert__price').textContent = free ? 'Included' : `+${inr(c.price)}`;
      if (free) b.classList.add('is-free');
      b.addEventListener('click', () => {
        if (sel.value === c.id) return;
        sel.value = c.id;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        sync();
      });
      list.appendChild(b);
      return b;
    });
    const sync = () => cards.forEach((b) => {
      const on = b.dataset.value === sel.value;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-checked', String(on));
    });
    sel.addEventListener('change', sync);
    const note = document.createElement('p');
    note.className = 'bt-certs__note';
    note.textContent = 'Lab reports confirm the stone is natural and state any treatment. IIGJ-GTL and IGI reports are recognised internationally.';
    sel.classList.add('bt-visually-hidden');
    sel.setAttribute('tabindex', '-1');
    sel.setAttribute('aria-hidden', 'true');
    const label = section.querySelector('label[for="gj-certificate"]');
    if (label) label.removeAttribute('for');
    section.appendChild(list);
    section.appendChild(note);
    sync();
  };

  // Wait for GJ's script (deferred) to render the widget, then add our buttons row
  const enhance = () => {
    const s = submit();
    if (!s || s.dataset.btDone) return !!s;
    s.dataset.btDone = '1';
    decorateForms();
    certCards();
    const row = document.createElement('div');
    row.className = 'bt-cfg__actions';
    s.parentNode.insertBefore(row, s);
    row.appendChild(s);
    if (wrap.dataset.buyNow !== 'false') {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bt-cfg__buy';
      b.textContent = 'Buy now';
      b.addEventListener('click', buyNow);
      row.appendChild(b);
      // Mirror the Add to cart button's enabled state
      new MutationObserver(() => { if (!buying) b.disabled = s.disabled; }).observe(s, { attributes: true, attributeFilter: ['disabled'] });
      b.disabled = s.disabled;
    }
    // A failed add shows an error message: stop the checkout redirect
    const msg = root.querySelector('[data-message]');
    if (msg) new MutationObserver(() => { if (buying && msg.classList.contains('is-error')) resetBuy(); }).observe(msg, { attributes: true, attributeFilter: ['class'] });
    // GJ's success message, in the site's own wording
    if (msg) new MutationObserver(() => { if (msg.textContent === 'Added to cart!') msg.textContent = 'Added to your cart.'; }).observe(msg, { childList: true, characterData: true, subtree: true });
    return true;
  };
  if (!enhance()) {
    const mo = new MutationObserver(() => { if (enhance()) mo.disconnect(); });
    mo.observe(root, { childList: true, subtree: true });
  }

  // Sticky bar buttons → configurator (capture phase, before Horizon's and bt-v6's own handlers)
  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const add = t.closest('sticky-add-to-cart .sticky-add-to-cart__button');
    const buy = t.closest('sticky-add-to-cart .bt-sticky-buy');
    if (!add && !buy) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const s = submit();
    if (!s || s.disabled) { showOptions(); return; }
    if (buy) buyNow();
    else s.click();
  }, true);

  window.addEventListener('pageshow', (e) => { if (e.persisted) resetBuy(); });
}
